import React, { useState, useEffect } from "react";
import { useSelector } from "react-redux";
import FormField from "../../../components/ui/FormField";
import { Button } from "../../../components/Button";
import { ArrowLeft, Trash2 } from "lucide-react";

// TODO: Replace this mock API call with your actual service when ready
const mockSubmitSh2Form = async (payload, accountId, token) => {
  console.log("Mock API call - Submit SH-2:", { payload, accountId, token });
  // Simulate network delay
  await new Promise((resolve) => setTimeout(resolve, 500));
  return { success: true };
};

const ShTwoForm = () => {
  // Redux data
  const token = useSelector((state) => state.auth.access_token);
  const companies = useSelector((state) => state.account.accountDetails);

  // Local state
  const [entries, setEntries] = useState(() => {
    const saved = localStorage.getItem("sh2_register_data");
    return saved ? JSON.parse(saved) : [{ id: Date.now() }];
  });
  const [accountId, setAccountId] = useState("");
  const [loading] = useState(false); // for company select (optional)
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState(null);

  // Persist entries to localStorage whenever they change
  useEffect(() => {
    localStorage.setItem("sh2_register_data", JSON.stringify(entries));
  }, [entries]);

  // ----- Handlers -----
  const updateField = (id, field, value) => {
    setEntries((prev) =>
      prev.map((entry) =>
        entry.id === id ? { ...entry, [field]: value } : entry
      )
    );
  };

  const addEntry = () => {
    setEntries([...entries, { id: Date.now() }]);
  };

  const deleteEntry = (id) => {
    if (entries.length > 1) {
      setEntries(entries.filter((e) => e.id !== id));
    }
  };

  const handleCompanyChange = (e) => {
    const selectedValue = e.target.value;
    setAccountId(selectedValue);
    const selectedCompany = companies.find((c) => c.id === selectedValue);
    if (selectedCompany) {
      console.log("Selected company:", selectedCompany.accountName);
    }
  };

  // Map frontend entry to API payload
  const mapEntryToRequest = (entry) => {
    return {
      sno: entry.sno || "",
      folioNo: entry.folioNo || "",
      recipientName: entry.recipientName || "",
      approvalDate: entry.approvalDate || "",
      shareClass: entry.shareClass || "",
      origIssueDate: entry.origIssueDate || "",
      origCertNo: entry.origCertNo || "",
      origTotalShares: entry.origTotalShares || "",
      distFrom: entry.distFrom || "",
      distTo: entry.distTo || "",
      renewedIssueDate: entry.renewedIssueDate || "",
      reasons: entry.reasons || "",
      renewedCertNo: entry.renewedCertNo || "",
      renewedTotalShares: entry.renewedTotalShares || "",
      refMembers: entry.refMembers || "",
      remarks: entry.remarks || "",
    };
  };

  const handleSave = async () => {
    if (!accountId) {
      setSubmitStatus({
        type: "error",
        message: "Please select a company before saving.",
      });
      return;
    }

    setIsSubmitting(true);
    setSubmitStatus(null);

    try {
      // Send one request per entry using mock API (replace with actual service)
      for (const entry of entries) {
        const payload = mapEntryToRequest(entry);
        await mockSubmitSh2Form(payload, accountId, token);
      }

      setSubmitStatus({
        type: "success",
        message: "All SH-2 register entries saved successfully!",
      });
      // Clear local storage after successful save
      localStorage.removeItem("sh2_register_data");
      // Optional: reset entries form
      // setEntries([{ id: Date.now() }]);
    } catch (error) {
      setSubmitStatus({
        type: "error",
        message:
          error.message || "Failed to save SH-2 register data. Please try again.",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="max-w-7xl mx-auto p-4 md:p-8 bg-gray-50 min-h-screen font-sans text-gray-800">
      {/* Header with Back button */}
      <div className="bg-white border-b-4 p-3 mb-2 text-center rounded-xl relative">
        <button
          onClick={() => window.history.back()}
          className="absolute top-3 left-4 flex items-center gap-2 text-gray-600 hover:text-gray-900"
        >
          <ArrowLeft size={18} />
          <span className="text-sm font-medium">Back</span>
        </button>
        <h1 className="text-2xl font-black uppercase">Form No. SH - 2</h1>
        <h2 className="text-lg font-bold mt-0">
          Register of Renewed and Duplicate Share Certificates
        </h2>
        <p className="text-[10px] italic text-slate-500 mt-0">
          [Pursuant to sub-section (3) of section 46 of the Companies Act, 2013
          and rule 6(3)(a) the Companies (Share Capital and Debentures) Rules
          2014]
        </p>
      </div>

      {/* Company Selection */}
      <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-4 mb-6">
        <label className="block text-sm font-medium text-gray-700 mb-1">
          Name of the company *
        </label>
        <select
          value={accountId}
          onChange={handleCompanyChange}
          disabled={loading}
          className="w-64 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
        >
          <option value="">Select a company</option>
          {companies.map((company) => (
            <option key={company.id} value={company.id}>
              {company.accountName || `Company ${company.id}`}
            </option>
          ))}
        </select>
      </div>

      {/* Status Message */}
      {submitStatus && (
        <div
          className={`mb-6 px-4 py-3 rounded-lg text-sm font-medium border ${
            submitStatus.type === "success"
              ? "bg-green-50 text-green-800 border-green-200"
              : "bg-red-50 text-red-800 border-red-200"
          }`}
        >
          {submitStatus.message}
        </div>
      )}

      {/* Entries List */}
      <div className="space-y-6">
        {entries.map((entry, index) => (
          <div
            key={entry.id}
            className="bg-white border border-gray-200 rounded-xl shadow-md overflow-hidden transition-all hover:shadow-lg"
          >
            <div className="bg-primary text-white px-6 py-3 flex justify-between items-center">
              <span className="font-bold text-xs uppercase tracking-widest">
                Entry Serial No: {index + 1}
              </span>
              {entries.length > 1 && (
                <button
                  onClick={() => deleteEntry(entry.id)}
                  className="text-xs bg-red-600 px-3 py-1 rounded hover:bg-red-700 font-bold uppercase flex items-center gap-1"
                >
                  <Trash2 size={14} /> Remove
                </button>
              )}
            </div>

            <div className="p-4 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              <FormField
                label="(1) S. No."
                value={entry.sno}
                onChange={(v) => updateField(entry.id, "sno", v)}
              />
              <FormField
                label="(2) Folio No."
                value={entry.folioNo}
                onChange={(v) => updateField(entry.id, "folioNo", v)}
              />
              <FormField
                label="(3) Name of the person(s) to whom Renewed / Duplicate share certificate is issued"
                value={entry.recipientName}
                onChange={(v) => updateField(entry.id, "recipientName", v)}
              />
              <FormField
                label="(4) Date of approval of issue of Renewed/ Duplicate share certificate"
                type="date"
                value={entry.approvalDate}
                onChange={(v) => updateField(entry.id, "approvalDate", v)}
              />
              <FormField
                label="(5) Class of shares"
                value={entry.shareClass}
                onChange={(v) => updateField(entry.id, "shareClass", v)}
              />
              <FormField
                label="(6) Date of issue of original share certificate"
                type="date"
                value={entry.origIssueDate}
                onChange={(v) => updateField(entry.id, "origIssueDate", v)}
              />
              <FormField
                label="(7) Original share certificate number"
                value={entry.origCertNo}
                onChange={(v) => updateField(entry.id, "origCertNo", v)}
              />
              <FormField
                label="(8) Total number of shares in the Original Share Certificate"
                type="number"
                value={entry.origTotalShares}
                onChange={(v) => updateField(entry.id, "origTotalShares", v)}
              />

              {/* (9) Distinctive Numbers */}
              <div className="p-3 border-2 border-slate-100 rounded-lg bg-slate-50/50">
                <label className="block text-[10px] font-black text-slate-400 uppercase mb-1">
                  (9) Distinctive No. of shares
                </label>
                <div className="grid grid-cols-2 gap-2">
                  <FormField
                    label="From"
                    value={entry.distFrom}
                    onChange={(v) => updateField(entry.id, "distFrom", v)}
                  />
                  <FormField
                    label="To"
                    value={entry.distTo}
                    onChange={(v) => updateField(entry.id, "distTo", v)}
                  />
                </div>
              </div>

              <FormField
                label="(10) Date of issue of Renewed/ Duplicate Share Certificate"
                type="date"
                value={entry.renewedIssueDate}
                onChange={(v) => updateField(entry.id, "renewedIssueDate", v)}
              />
              <FormField
                label="(11) Reasons for issue of Renewed/ Duplicate Share Certificate"
                type="description"
                value={entry.reasons}
                onChange={(v) => updateField(entry.id, "reasons", v)}
              />
              <FormField
                label="(12) Number of the Renewed share certificate, if applicable"
                value={entry.renewedCertNo}
                onChange={(v) => updateField(entry.id, "renewedCertNo", v)}
              />
              <FormField
                label="(13) Total Number of Shares in the Renewed/ Duplicate Share Certificate"
                type="number"
                value={entry.renewedTotalShares}
                onChange={(v) => updateField(entry.id, "renewedTotalShares", v)}
              />
              <FormField
                label="(14) Reference to entry in Register of Members"
                value={entry.refMembers}
                onChange={(v) => updateField(entry.id, "refMembers", v)}
              />
              <FormField
                label="(15) Remarks"
                type="description"
                value={entry.remarks}
                onChange={(v) => updateField(entry.id, "remarks", v)}
              />
            </div>
          </div>
        ))}
      </div>

      {/* Action Buttons */}
      <div className="flex w-full justify-between items-center mt-8">
        <Button label="Add New SH-2" onClick={addEntry} className="mt-2" />
        <Button
          label={isSubmitting ? "Saving..." : "Save Register Data"}
          onClick={handleSave}
          disabled={isSubmitting}
          variant="primary"
          className="mt-2"
        />
      </div>
    </div>
  );
};

export default ShTwoForm;